import { html, nothing, LitElement, type TemplateResult, type PropertyValues } from "lit";
import { customElement, state } from "lit/decorators.js";

import "./components/dashboard-header.ts";
import "./components/app-settings-modal.ts";
import "./components/operator-dialog-host.ts";
import "./views/projects.ts";
import "./views/ead-diagram-standard-view.ts";
import "./views/ead-skills.ts";
import "./views/ead-workflow-rules.ts";
import "./views/runs.ts";
import "./views/closed-runs.ts";
import "./views/chat.ts";
import "./views/chat-minimal.ts";
import "./views/explore-types.ts";
import "./views/generate-chat.ts";
import type { Tab } from "./app-view-state.js";
import type { ThemeMode, ThemeName } from "./theme-document.js";
import { icons } from "./icons.ts";
import { applyThemeToDocument, loadThemeSettings, saveThemeSettings } from "./theme-document.ts";
import {
  ExecutionsStore,
  createTemplateOnlySidebarRun,
  executionsStore,
  isTemplateOnlySidebarRun,
  pickInteractiveRunForTemplate,
} from "./executions-store.ts";
import { hermesClient, HERMES_BASE_URL } from "./hermes-singleton.ts";
import {
  hasGatewayEverSucceeded,
  isGatewayReachable,
  recordGatewayFailure,
  recordGatewaySuccess,
} from "./gateway-health.ts";
import { canPollGateway } from "./poll-coordinator.ts";
import { formatDurationHuman } from "./format.ts";
import { budgetCountdownStartMs } from "./run-budget-clock.ts";
import {
  isLiveProjectExecute,
  ProjectRunStatus,
  projectRunStatusSidebarClass,
  storedProjectRunStatus,
} from "./run-status.ts";
import { saveCachedTemplateSummaries, templateFromListSummary } from "./template-list-cache.ts";
import type { ProjectExecute } from "../types/ead-types.ts";
import { getLocale, setLocale, subscribeLocale, t, type UiLocale } from "./i18n.ts";

const SHELL_KEY = "ead-expui.shell.v1";
const ACTIVE_TAB_KEY = "ead-expui.active-tab.v1";
const GATEWAY_EVER_CONNECTED_KEY = "ead-expui.gateway-ever-connected.v1";

const VALID_TABS: ReadonlySet<Tab> = new Set([
  "eadDiagramStandard",
  "eadSkills",
  "eadWorkflowRules",
  "exploreTypes",
  "projects",
  "runs",
  "closedRuns",
  "skills",
  "chat",
  "chatMinimal",
  "generateChat",
]);

function loadNavCollapsed(): boolean {
  try {
    const raw = localStorage.getItem(SHELL_KEY);
    if (!raw) return false;
    const s = JSON.parse(raw) as { navCollapsed?: boolean };
    return Boolean(s.navCollapsed);
  } catch {
    return false;
  }
}

function saveNavCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(SHELL_KEY, JSON.stringify({ navCollapsed: collapsed }));
  } catch {
    /* ignore */
  }
}

function loadActiveTab(): Tab {
  try {
    const raw = localStorage.getItem(ACTIVE_TAB_KEY);
    if (!raw) return "projects";
    if (raw === "reports") {
      const next: Tab = "runs";
      saveActiveTab(next);
      return next;
    }
    if (raw === "chat" || raw === "chatMinimal") {
      const next: Tab = "generateChat";
      saveActiveTab(next);
      return next;
    }
    if (VALID_TABS.has(raw as Tab)) return raw as Tab;
  } catch {
    /* ignore */
  }
  return "projects";
}

function saveActiveTab(tab: Tab): void {
  try {
    localStorage.setItem(ACTIVE_TAB_KEY, tab);
  } catch {
    /* ignore */
  }
}

function loadGatewayEverConnected(): boolean {
  try {
    return localStorage.getItem(GATEWAY_EVER_CONNECTED_KEY) === "1";
  } catch {
    return false;
  }
}

function saveGatewayEverConnected(value: boolean): void {
  try {
    localStorage.setItem(GATEWAY_EVER_CONNECTED_KEY, value ? "1" : "0");
  } catch {
    /* ignore */
  }
}

function isChatTab(tab: Tab): boolean {
  return tab === "generateChat";
}

@customElement("ead-expui-app")
export class EadExpUiApp extends LitElement {
  /**
   * Shell layout (.shell, sidebar, grid) lives in imported global CSS (`app-global.css`).
   * Light DOM ensures those rules apply; shadow DOM blocks document-level selectors.
   */
  override createRenderRoot() {
    return this;
  }

  @state() private _tab: Tab = loadActiveTab();
  /** Optimistic online after a prior session so refresh does not flash Offline while /health queues. */
  @state() private _hermesConnected = loadGatewayEverConnected();
  @state() private _gatewayEverConnected = loadGatewayEverConnected();

  /** EAD-Explore-aligned shell */
  @state() private _navCollapsed = loadNavCollapsed();
  @state() private _navDrawerOpen = false;
  @state() private _theme: ThemeName = "claw";
  @state() private _themeMode: ThemeMode = "system";
  @state() private _locale: UiLocale = getLocale();
  @state() private _langMenuOpen = false;
  @state() private _settingsOpen = false;
  @state() private _activeRunSearch = "";
  @state() private _interactiveRunSearch = "";
  @state() private _interactiveRunPage = 0;
  @state() private _templateNameById: Record<string, string> = {};
  /** Bumps every 1s while any active run has a live budget countdown (sidebar). */
  @state() private _sidebarClockTick = 0;

  private _sidebarCountdownInterval: ReturnType<typeof setInterval> | null = null;

  private _prefersMq: MediaQueryList | null =
    typeof globalThis.matchMedia === "function"
      ? globalThis.matchMedia("(prefers-color-scheme: light)")
      : null;
  private _onPrefersSchemeChange = () => {
    if (this._themeMode === "system") {
      applyThemeToDocument(this._theme, "system");
    }
  };
  private _unsubExecutions: (() => void) | null = null;
  /** Re-poll /health so the shell shows Online after you start the gateway (no full refresh). */
  private _healthPollInterval: ReturnType<typeof setInterval> | null = null;
  private _healthCheckInFlight = false;
  private _healthFailureStreak = 0;
  private _wasGatewayOnline = false;
  private _lastSidebarEmptyRetryAt = 0;
  private _unsubLocale: (() => void) | null = null;
  private _onWindowMouseDown = (event: MouseEvent) => {
    if (!this._langMenuOpen) return;
    const target = event.target as Node | null;
    const wrap = this.querySelector(".topbar-lang-wrap");
    if (wrap && target && !wrap.contains(target)) {
      this._langMenuOpen = false;
    }
  };

  constructor() {
    super();
    const s = loadThemeSettings();
    this._theme = s.theme;
    this._themeMode = s.themeMode;
  }

  connectedCallback() {
    super.connectedCallback();
    this._locale = getLocale();
    applyThemeToDocument(this._theme, this._themeMode);
    void this._checkConnection();
    void this._loadTemplateNames();
    executionsStore.setHeavyPollingEnabled(ExecutionsStore.shouldPollForTab(this._tab));
    void executionsStore.fetchActiveExecutionProbe();
    this._healthPollInterval = window.setInterval(() => void this._checkConnection(), 15_000);
    this._prefersMq?.addEventListener("change", this._onPrefersSchemeChange);
    this._unsubExecutions = executionsStore.subscribe(() => {
      const reachable = isGatewayReachable();
      if (reachable !== this._hermesConnected) {
        this._hermesConnected = reachable;
      }
      this.requestUpdate();
    });
    this._unsubLocale = subscribeLocale((locale) => {
      this._locale = locale;
    });
    window.addEventListener("mousedown", this._onWindowMouseDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._sidebarCountdownInterval != null) {
      window.clearInterval(this._sidebarCountdownInterval);
      this._sidebarCountdownInterval = null;
    }
    if (this._healthPollInterval != null) {
      window.clearInterval(this._healthPollInterval);
      this._healthPollInterval = null;
    }
    this._prefersMq?.removeEventListener("change", this._onPrefersSchemeChange);
    this._unsubExecutions?.();
    this._unsubExecutions = null;
    this._unsubLocale?.();
    this._unsubLocale = null;
    window.removeEventListener("mousedown", this._onWindowMouseDown);
  }

  updated(changed: PropertyValues) {
    super.updated(changed);
    if (changed.has("_tab")) {
      executionsStore.setHeavyPollingEnabled(ExecutionsStore.shouldPollForTab(this._tab));
    }
    void this._sidebarClockTick;
    const runs = executionsStore.activeRuns;
    const needTicker = runs.some((r) => {
      if (!isLiveProjectExecute(r)) return false;
      const budgetMin = Number(r.timeBudgetMinutes || 0);
      if (budgetMin <= 0) return false;
      return storedProjectRunStatus(r) === ProjectRunStatus.Running;
    });
    if (needTicker && this._sidebarCountdownInterval == null) {
      this._sidebarCountdownInterval = window.setInterval(() => {
        this._sidebarClockTick = Date.now();
      }, 1000);
    } else if (!needTicker && this._sidebarCountdownInterval != null) {
      window.clearInterval(this._sidebarCountdownInterval);
      this._sidebarCountdownInterval = null;
    }
  }

  private async _checkConnection() {
    if (this._healthCheckInFlight) return;
    if (!canPollGateway()) return;
    this._healthCheckInFlight = true;
    let ok = false;
    let timeout: ReturnType<typeof setTimeout> | null = null;
    try {
      const controller = new AbortController();
      timeout = window.setTimeout(() => controller.abort(), 8000);
      const healthUrl = HERMES_BASE_URL ? `${HERMES_BASE_URL}/health` : "/health";
      const resp = await fetch(healthUrl, { signal: controller.signal });
      ok = resp.ok;
    } catch {
      ok = false;
    } finally {
      if (timeout != null) window.clearTimeout(timeout);
      this._healthCheckInFlight = false;
    }
    const wasOnline = this._wasGatewayOnline;
    if (ok) {
      this._healthFailureStreak = 0;
      recordGatewaySuccess();
      this._hermesConnected = true;
    } else {
      this._healthFailureStreak += 1;
      recordGatewayFailure();
      const reachable = isGatewayReachable();
      if (this._healthFailureStreak >= 3 && !reachable) {
        this._hermesConnected = false;
      } else if (reachable) {
        this._hermesConnected = true;
      }
    }
    this._wasGatewayOnline = ok && this._hermesConnected;
    if (this._wasGatewayOnline) {
      if (!wasOnline) {
        executionsStore.onGatewayReachable();
      } else if (
        executionsStore.interactiveSidebarRuns.length === 0
        && Date.now() - this._lastSidebarEmptyRetryAt > 15_000
      ) {
        this._lastSidebarEmptyRetryAt = Date.now();
        void executionsStore.fetchInteractiveSidebar();
      }
    }
    if (this._hermesConnected || hasGatewayEverSucceeded()) {
      if (!this._gatewayEverConnected) {
        this._gatewayEverConnected = true;
        saveGatewayEverConnected(true);
      }
    }
    this.requestUpdate();
  }

  private _setTab(tab: Tab) {
    this._tab = tab;
    saveActiveTab(tab);
  }

  private _topbarContextLabel(tab: Tab): string {
    if (tab !== "runs") return "";
    const selected = executionsStore.selectedExecution;
    return selected ? this._displayRunTitle(selected) : "";
  }

  private _persistTheme(theme: ThemeName, mode: ThemeMode) {
    this._theme = theme;
    this._themeMode = mode;
    saveThemeSettings({ theme, themeMode: mode });
    applyThemeToDocument(theme, mode);
  }

  private _setThemeFamily(theme: ThemeName) {
    this._persistTheme(theme, this._themeMode);
  }

  private _setUiLocale(locale: UiLocale, event?: Event): void {
    if (event) event.preventDefault();
    this._locale = setLocale(locale);
    if (event) {
      const details = (event.currentTarget as HTMLElement | null)?.closest("details");
      if (details instanceof HTMLDetailsElement) details.open = false;
    }
  }

  private _localeDisplayName(locale: UiLocale): string {
    if (locale === "zh") return t("lang.zh", undefined, this._locale);
    if (locale === "ja") return t("lang.ja", undefined, this._locale);
    if (locale === "ko") return t("lang.ko", undefined, this._locale);
    return t("lang.en", undefined, this._locale);
  }

  private _themeDisplayName(theme: ThemeName): string {
    if (theme === "claw") return "Classic";
    if (theme === "knot") return "Ocean";
    return "Dashboard";
  }

  private _themeGlyph(theme: ThemeName): string {
    if (theme === "claw") return "C";
    if (theme === "knot") return "O";
    return "D";
  }

  private _selectThemeFromPicker(theme: ThemeName, event: Event): void {
    event.preventDefault();
    this._setThemeFamily(theme);
    const details = (event.currentTarget as HTMLElement | null)?.closest("details");
    if (details instanceof HTMLDetailsElement) {
      details.open = false;
    }
  }

  private _setAppearanceMode(mode: ThemeMode) {
    this._persistTheme(this._theme, mode);
  }

  private _toggleNavCollapsed() {
    const next = !this._navCollapsed;
    this._navCollapsed = next;
    saveNavCollapsed(next);
  }

  /** Lucide-aligned nav row (EAD-Explore sidebar item pattern) */
  private _navItem(tab: Tab, icon: TemplateResult, label: string) {
    const active = this._tab === tab;
    const collapsed = this._navCollapsed;
    return html`
      <button
        type="button"
        class="nav-item ${active ? "active" : ""}"
        @click=${() => {
          this._setTab(tab);
          this._navDrawerOpen = false;
        }}
      >
        <span class="nav-item__icon" aria-hidden="true">${icon}</span>
        ${collapsed ? nothing : html`<span class="nav-item__text">${label}</span>`}
      </button>
    `;
  }

  private _shellClass(tab: Tab): string {
    const shellChat = isChatTab(tab) ? "shell--chat" : "";
    const navCol = this._navCollapsed ? "shell--nav-collapsed" : "";
    const drawer = this._navDrawerOpen ? "shell--nav-drawer-open" : "";
    return ["shell", shellChat, navCol, drawer].filter(Boolean).join(" ");
  }

  private _runStatusClass(run: ProjectExecute): string {
    return projectRunStatusSidebarClass(run);
  }

  private _runInvalidReason(run: ProjectExecute): string {
    return (
      run.invalidForDataReportingTrainingReason ||
      run.learningExclusionReason ||
      "Not valid for data reporting and training"
    );
  }

  private async _loadTemplateNames(): Promise<void> {
    const result = await hermesClient.request<{ templates?: Array<Record<string, unknown>> }>(
      "templates.list",
    );
    const rows = Array.isArray(result?.templates) ? result.templates : [];
    const next: Record<string, string> = {};
    for (const row of rows) {
      const id = String(row?.id || "").trim();
      const name = String(row?.name || "").trim();
      if (!id || !name) continue;
      next[id] = name;
    }
    this._templateNameById = next;
    if (rows.length) {
      saveCachedTemplateSummaries(rows.map((row) => templateFromListSummary(row)));
      void executionsStore.fetchInteractiveSidebar();
      this.requestUpdate();
    }
  }

  private _displayRunTitle(run: ProjectExecute): string {
    const rawName = String(run.name || "").trim();
    const baseName = rawName.replace(/^Run:\s*/i, "").trim() || "Untitled Run";
    const runNumber = Number(run.runNumber || 0);
    if (Number.isFinite(runNumber) && runNumber > 0) {
      return `Run ${runNumber}, ${baseName}`;
    }
    return baseName;
  }

  private _formatSidebarCountdown(remainingMs: number): string {
    const totalSec = Math.max(0, Math.floor(remainingMs / 1000));
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }

  /** Sidebar subtitle: host · elapsed for finished runs, host · MM:SS left for active budgeted runs. */
  private _runSidebarMeta(run: ProjectExecute): string {
    const rawTarget = (run.targetUrl || "").trim();
    let host = "No target";
    if (rawTarget) {
      try {
        host = new URL(rawTarget).hostname || rawTarget;
      } catch {
        host = rawTarget;
      }
    }
    const active = isLiveProjectExecute(run);
    const budgetMin = Number(run.timeBudgetMinutes || 0);
    const executorRunning = storedProjectRunStatus(run) === ProjectRunStatus.Running;
    if (active && budgetMin > 0) {
      const budgetMs = budgetMin * 60 * 1000;
      const budgetStart = budgetCountdownStartMs(run);
      if (executorRunning && budgetStart != null) {
        const elapsed = Math.max(0, Date.now() - budgetStart);
        const remainingMs = Math.max(0, budgetMs - elapsed);
        void this._sidebarClockTick;
        return `${host} · ${this._formatSidebarCountdown(remainingMs)} left`;
      }
      return `${host} · ${this._formatSidebarCountdown(budgetMs)}`;
    }
    const duration =
      typeof run.durationMs === "number" && Number.isFinite(run.durationMs)
        ? formatDurationHuman(run.durationMs)
        : "—";
    return `${host} · ${duration}`;
  }

  private _isRunClosed(run: ProjectExecute): boolean {
    if (typeof run.chatDeliveryActive === "boolean") return !run.chatDeliveryActive;
    const activity = String(run.reportingActivityStatus || "").trim().toLowerCase();
    if (activity === "closed") return true;
    if (activity === "active") return false;
    return !isLiveProjectExecute(run);
  }

  private _templateNameForRun(run: ProjectExecute): string {
    const templateId = String(run.linkedTemplateId || "").trim();
    const fromMap = templateId ? String(this._templateNameById[templateId] || "").trim() : "";
    if (fromMap) return fromMap;
    return String(this._displayRunTitle(run)).replace(/^Run\s+\d+\s*,\s*/i, "").trim() || "Unnamed template";
  }

  private _interactiveRuns(): ProjectExecute[] {
    const indexed = executionsStore.interactiveSidebarRuns;
    if (indexed.length) return indexed;
    return this._interactiveRunsFromExecutions();
  }

  /** Legacy client-side grouping when interactive-sidebar API is unavailable. */
  private _interactiveRunsFromExecutions(): ProjectExecute[] {
    const sortedRuns = [...executionsStore.executions].sort((a, b) => {
      const aRunNo = Number(a.runNumber || 0);
      const bRunNo = Number(b.runNumber || 0);
      if (Number.isFinite(aRunNo) && Number.isFinite(bRunNo) && (aRunNo > 0 || bRunNo > 0)) {
        if (bRunNo !== aRunNo) return bRunNo - aRunNo;
      }
      const aCreated = Number(
        (a as { createdAt?: number; created_at?: number }).createdAt ??
          (a as { created_at?: number }).created_at ??
          0,
      );
      const bCreated = Number(
        (b as { createdAt?: number; created_at?: number }).createdAt ??
          (b as { created_at?: number }).created_at ??
          0,
      );
      if (bCreated !== aCreated) return bCreated - aCreated;
      return Number(b.startTime || 0) - Number(a.startTime || 0);
    });
    const byTemplate = new Map<string, ProjectExecute[]>();
    for (const run of sortedRuns) {
      const templateId = String(run.linkedTemplateId || "").trim();
      if (!templateId) continue;
      const rows = byTemplate.get(templateId) || [];
      rows.push(run);
      byTemplate.set(templateId, rows);
    }
    const templateIds = new Set<string>([
      ...Object.keys(this._templateNameById),
      ...Array.from(byTemplate.keys()),
    ]);
    const orderedTemplateIds = Array.from(templateIds).sort((a, b) =>
      (this._templateNameById[a] || a).localeCompare(this._templateNameById[b] || b),
    );
    const selected: ProjectExecute[] = [];
    for (const templateId of orderedTemplateIds) {
      const runs = byTemplate.get(templateId) || [];
      const picked = pickInteractiveRunForTemplate(runs);
      if (picked) {
        selected.push(picked);
        continue;
      }
      const templateName = this._templateNameById[templateId] || templateId;
      selected.push(createTemplateOnlySidebarRun(templateId, templateName));
    }
    return selected;
  }

  private _filterRuns(rows: ProjectExecute[], searchValue: string): ProjectExecute[] {
    const q = searchValue.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((run) =>
      [run.name, run.targetUrl, run.description, this._templateNameForRun(run)]
        .map((v) => (v || "").toLowerCase())
        .some((v) => v.includes(q)),
    );
  }

  private _renderRunGroup(
    rows: ProjectExecute[],
    searchValue: string,
    onSearch: (next: string) => void,
    pageIndex: number,
    onSetPage: (next: number) => void,
    options?: {
      pageSize?: number;
      searchPlaceholder?: string;
      titleForRun?: (run: ProjectExecute) => string;
      metaForRun?: (run: ProjectExecute) => string;
      onSelectRun?: (run: ProjectExecute) => void;
      emptyWhenNoRows?: string;
    },
  ) {
    const filteredRows = this._filterRuns(rows, searchValue);
    const pageSize = Math.max(1, Number(options?.pageSize || 25));
    const pageCount = Math.max(1, Math.ceil(filteredRows.length / pageSize));
    const safePage = Math.max(0, Math.min(pageIndex, pageCount - 1));
    const pageRows = filteredRows.slice(safePage * pageSize, safePage * pageSize + pageSize);
    const selectedId = executionsStore.selectedExecutionId;
    return html`
      <div class="sidebar-run-group">
        <div class="sidebar-run-group__header">
          <input
            class="sidebar-run-group__search"
            type="search"
            placeholder=${options?.searchPlaceholder || t("nav.searchRuns", undefined, this._locale)}
            .value=${searchValue}
            @input=${(ev: Event) => {
              const next = (ev.target as HTMLInputElement).value || "";
              onSearch(next);
              onSetPage(0);
            }}
          />
          <div class="sidebar-run-group__pager">
            <button
              type="button"
              class="sidebar-run-group__pager-btn"
              ?disabled=${safePage <= 0}
              @click=${() => onSetPage(safePage - 1)}
              aria-label="Previous page"
            >
              &lt;
            </button>
            <span class="sidebar-run-group__pager-page">${safePage + 1}/${pageCount}</span>
            <button
              type="button"
              class="sidebar-run-group__pager-btn"
              ?disabled=${safePage >= pageCount - 1}
              @click=${() => onSetPage(safePage + 1)}
              aria-label="Next page"
            >
              &gt;
            </button>
          </div>
        </div>
        <div class="sidebar-run-group__list">
          ${pageRows.length === 0
            ? html`<div class="sidebar-run-group__empty"
                >${rows.length === 0
                  ? options?.emptyWhenNoRows || "No runs"
                  : "No matching runs"}</div
              >`
            : pageRows.map(
                (run) => html`
                  <button
                    type="button"
                    class="sidebar-run ${selectedId === run.id ? "sidebar-run--selected" : ""} ${run.validForDataReportingTraining === false ? "sidebar-run--invalid" : ""}"
                    @click=${() => {
                      if (options?.onSelectRun) {
                        options.onSelectRun(run);
                        return;
                      }
                      executionsStore.setSelectedExecutionId(run.id);
                      this._setTab("runs");
                      this._navDrawerOpen = false;
                    }}
                  >
                    <span
                      class="sidebar-run__status ${this._runStatusClass(run)}"
                      title=${run.status}
                    ></span>
                    <span class="sidebar-run__content">
                      <span class="sidebar-run__title-row">
                        <span class="sidebar-run__title">${options?.titleForRun ? options.titleForRun(run) : this._displayRunTitle(run)}</span>
                        ${run.validForDataReportingTraining === false
                          ? html`
                              <span
                                class="sidebar-run__invalid-icon"
                                title=${this._runInvalidReason(run)}
                                aria-label="Not valid for data reporting and training"
                              >
                                ${icons.circleSlash}
                              </span>
                            `
                          : nothing}
                      </span>
                      <span class="sidebar-run__meta"
                        >${run.validForDataReportingTraining === false
                          ? "Not valid for reporting/training"
                          : options?.metaForRun
                            ? options.metaForRun(run)
                            : this._runSidebarMeta(run)}</span
                      >
                    </span>
                  </button>
                `,
              )}
        </div>
      </div>
    `;
  }

  render() {
    const tab = this._tab;
    const collapsed = this._navCollapsed;
    const interactiveRuns = this._interactiveRuns();

    const contentClass = ["content", isChatTab(tab) ? "content--chat" : ""].filter(Boolean).join(
      " ",
    );

    return html`
      <div class="ead-app-root" style="display:contents">
      <div class=${this._shellClass(tab)}>
        <button
          type="button"
          class="shell-nav-backdrop"
          aria-label=${t("app.closeNavigation", undefined, this._locale)}
          @click=${() => {
            this._navDrawerOpen = false;
          }}
        ></button>

        <header class="topbar">
          <div class="topnav-shell">
            <button
              type="button"
              class="topbar-nav-toggle"
              @click=${() => {
                this._navDrawerOpen = !this._navDrawerOpen;
              }}
              aria-label="${this._navDrawerOpen ? t("app.closeMenu", undefined, this._locale) : t("app.openMenu", undefined, this._locale)}"
              aria-expanded=${this._navDrawerOpen}
            >
              <span class="nav-collapse-toggle__icon" aria-hidden="true">${icons.menu}</span>
            </button>
            <div class="topnav-shell__content">
              <dashboard-header
                class="dashboard-header-component"
                .tab=${tab}
                .contextLabel=${this._topbarContextLabel(tab)}
                brandLabel=${t("brand.name", undefined, this._locale)}
                homeTab="projects"
                @navigate=${(e: CustomEvent<Tab>) => this._setTab(e.detail)}
              ></dashboard-header>
            </div>
            <div class="topnav-shell__actions">
              <div class="topbar-theme-mode" role="group" aria-label="Color appearance">
                <button
                  type="button"
                  class="topbar-theme-mode__btn ${this._themeMode === "system"
                    ? "topbar-theme-mode__btn--active"
                    : ""}"
                  title="System theme"
                  aria-label="System theme"
                  @click=${() => this._setAppearanceMode("system")}
                >
                  ${icons.monitor}
                </button>
                <button
                  type="button"
                  class="topbar-theme-mode__btn ${this._themeMode === "light"
                    ? "topbar-theme-mode__btn--active"
                    : ""}"
                  title="Light"
                  aria-label="Light"
                  @click=${() => this._setAppearanceMode("light")}
                >
                  ${icons.sun}
                </button>
                <button
                  type="button"
                  class="topbar-theme-mode__btn ${this._themeMode === "dark"
                    ? "topbar-theme-mode__btn--active"
                    : ""}"
                  title="Dark"
                  aria-label="Dark"
                  @click=${() => this._setAppearanceMode("dark")}
                >
                  ${icons.moon}
                </button>
              </div>
              <div class="topbar-theme-mode ead-expui-theme-row" role="group" aria-label="Theme family">
                <details class="topbar-theme-picker">
                  <summary
                    class="topbar-theme-mode__btn topbar-theme-picker__summary topbar-theme-mode__btn--active"
                    title=${`Theme: ${this._themeDisplayName(this._theme)}`}
                    aria-label=${`Current theme ${this._themeDisplayName(this._theme)}. Open theme menu`}
                  >
                    ${this._themeGlyph(this._theme)}
                  </summary>
                  <div class="topbar-theme-picker__menu" role="menu" aria-label="Theme selection">
                    <button
                      type="button"
                      role="menuitemradio"
                      aria-checked=${this._theme === "claw"}
                      class="topbar-theme-picker__item ${this._theme === "claw" ? "topbar-theme-picker__item--active" : ""}"
                      @click=${(event: Event) => this._selectThemeFromPicker("claw", event)}
                    >
                      Classic
                    </button>
                    <button
                      type="button"
                      role="menuitemradio"
                      aria-checked=${this._theme === "knot"}
                      class="topbar-theme-picker__item ${this._theme === "knot" ? "topbar-theme-picker__item--active" : ""}"
                      @click=${(event: Event) => this._selectThemeFromPicker("knot", event)}
                    >
                      Ocean
                    </button>
                    <button
                      type="button"
                      role="menuitemradio"
                      aria-checked=${this._theme === "dash"}
                      class="topbar-theme-picker__item ${this._theme === "dash" ? "topbar-theme-picker__item--active" : ""}"
                      @click=${(event: Event) => this._selectThemeFromPicker("dash", event)}
                    >
                      Dashboard
                    </button>
                  </div>
                </details>
              </div>
              <div class="topbar-theme-mode topbar-lang-wrap" role="group" aria-label=${t("lang.select", undefined, this._locale)}>
                <button
                  type="button"
                  class="topbar-theme-mode__btn topbar-lang-picker__summary ${this._langMenuOpen ? "topbar-theme-mode__btn--active" : ""}"
                  title=${`${t("lang.current", undefined, this._locale)}: ${this._localeDisplayName(this._locale)}`}
                  aria-label=${t("lang.select", undefined, this._locale)}
                  aria-haspopup="menu"
                  aria-expanded=${this._langMenuOpen}
                  @click=${() => {
                    this._langMenuOpen = !this._langMenuOpen;
                  }}
                >
                  ${icons.globe}
                  <span class="topbar-lang-code">${this._locale.toUpperCase()}</span>
                </button>
                <div
                  class="topbar-theme-picker__menu topbar-lang-picker__menu ${this._langMenuOpen ? "topbar-lang-picker__menu--open" : ""}"
                  role="menu"
                  aria-label=${t("lang.select", undefined, this._locale)}
                >
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked=${this._locale === "en"}
                    class="topbar-theme-picker__item ${this._locale === "en" ? "topbar-theme-picker__item--active" : ""}"
                    @click=${(event: Event) => {
                      this._setUiLocale("en", event);
                      this._langMenuOpen = false;
                    }}
                  >
                    ${t("lang.en", undefined, this._locale)} (EN)
                  </button>
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked=${this._locale === "zh"}
                    class="topbar-theme-picker__item ${this._locale === "zh" ? "topbar-theme-picker__item--active" : ""}"
                    @click=${(event: Event) => {
                      this._setUiLocale("zh", event);
                      this._langMenuOpen = false;
                    }}
                  >
                    ${t("lang.zh", undefined, this._locale)} (ZH)
                  </button>
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked=${this._locale === "ja"}
                    class="topbar-theme-picker__item ${this._locale === "ja" ? "topbar-theme-picker__item--active" : ""}"
                    @click=${(event: Event) => {
                      this._setUiLocale("ja", event);
                      this._langMenuOpen = false;
                    }}
                  >
                    ${t("lang.ja", undefined, this._locale)} (JA)
                  </button>
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked=${this._locale === "ko"}
                    class="topbar-theme-picker__item ${this._locale === "ko" ? "topbar-theme-picker__item--active" : ""}"
                    @click=${(event: Event) => {
                      this._setUiLocale("ko", event);
                      this._langMenuOpen = false;
                    }}
                  >
                    ${t("lang.ko", undefined, this._locale)} (KO)
                  </button>
                </div>
              </div>
              <div class="topbar-theme-mode topbar-settings-wrap" role="group" aria-label="App settings">
                <button
                  type="button"
                  class="topbar-theme-mode__btn"
                  title="Settings"
                  aria-label="Open settings"
                  @click=${() => {
                    this._settingsOpen = true;
                  }}
                >
                  ${icons.settings}
                </button>
              </div>
            </div>
          </div>
        </header>

        <div class="shell-nav">
          <aside class="sidebar ${collapsed ? "sidebar--collapsed" : ""}">
            <div class="sidebar-shell">
              <div class="sidebar-shell__header">
                <div class="sidebar-brand">
                  ${collapsed
                    ? nothing
                    : html`
                        <img class="sidebar-brand__logo" src="/logo.png" alt="" width="32" height="32" />
                        <span class="sidebar-brand__copy">
                          <span class="sidebar-brand__title">${t("brand.name", undefined, this._locale)}</span>
                        </span>
                      `}
                </div>
                <button
                  type="button"
                  class="nav-collapse-toggle"
                  title="${collapsed ? "Expand sidebar" : "Collapse sidebar"}"
                  aria-label="${collapsed ? "Expand sidebar" : "Collapse sidebar"}"
                  @click=${() => this._toggleNavCollapsed()}
                >
                  <span class="nav-collapse-toggle__icon" aria-hidden="true">
                    ${collapsed ? icons.panelLeftOpen : icons.panelLeftClose}
                  </span>
                </button>
              </div>
              <div class="sidebar-shell__body">
                <nav class="sidebar-nav" aria-label="Primary">
                  <section class="nav-section">
                    ${collapsed
                      ? nothing
                      : html`
                          <div class="nav-section__label" aria-hidden="true">
                            <span class="nav-section__label-text">${t("nav.explorePlanning", undefined, this._locale)}</span>
                          </div>
                        `}
                    <div class="nav-section__items" style=${collapsed ? "" : "padding-left:12px;"}>
                      ${this._navItem("eadDiagramStandard", icons.barChart, t("tab.eadDiagramStandard", undefined, this._locale))}
                      ${this._navItem("eadWorkflowRules", icons.settings, t("tab.eadWorkflowRules", undefined, this._locale))}
                      ${this._navItem("eadSkills", icons.book, t("tab.eadSkills", undefined, this._locale))}
                      ${this._navItem("exploreTypes", icons.brain, t("tab.exploreTypes", undefined, this._locale))}
                      ${this._navItem("projects", icons.folder, t("tab.projects", undefined, this._locale))}
                    </div>
                  </section>
                  <section class="nav-section">
                    ${collapsed
                      ? nothing
                      : html`
                          <div class="nav-section__label" aria-hidden="true">
                            <span class="nav-section__label-text">${t("nav.activeProjectRun", undefined, this._locale)}</span>
                          </div>
                        `}
                    ${collapsed
                      ? nothing
                      : html`
                          <div class="nav-section__items nav-section__items--runs">
                            ${this._renderRunGroup(
                              executionsStore.activeRuns,
                              this._activeRunSearch,
                              (next) => {
                                this._activeRunSearch = next;
                              },
                              executionsStore.activePageIndex,
                              (next) => executionsStore.setActivePage(next),
                            )}
                          </div>
                        `}
                  </section>
                  <section class="nav-section">
                    ${collapsed
                      ? nothing
                      : html`
                          <div class="nav-section__label" aria-hidden="true">
                            <span class="nav-section__label-text">${t("nav.projectInteractive", undefined, this._locale)}</span>
                          </div>
                        `}
                    ${collapsed
                      ? nothing
                      : html`
                          <div class="nav-section__items nav-section__items--runs">
                            ${this._renderRunGroup(
                              interactiveRuns,
                              this._interactiveRunSearch,
                              (next) => {
                                this._interactiveRunSearch = next;
                              },
                              this._interactiveRunPage,
                              (next) => { this._interactiveRunPage = Math.max(0, next); },
                              {
                                pageSize: 25,
                                searchPlaceholder: t("nav.searchTemplateRun", undefined, this._locale),
                                emptyWhenNoRows:
                                  !isGatewayReachable() && !hasGatewayEverSucceeded()
                                    ? "Cannot load templates — start the gateway"
                                    : "No project templates",
                                titleForRun: (run) => this._templateNameForRun(run),
                                metaForRun: (run) => {
                                  if (isTemplateOnlySidebarRun(run)) return t("nav.noRunsYet", undefined, this._locale);
                                  return this._isRunClosed(run)
                                    ? t("nav.noActiveRunLatest", { title: this._displayRunTitle(run) }, this._locale)
                                    : t("nav.interactiveRun", { title: this._displayRunTitle(run), meta: this._runSidebarMeta(run) }, this._locale);
                                },
                                onSelectRun: (run) => {
                                  if (isTemplateOnlySidebarRun(run)) {
                                    this._setTab("projects");
                                    this._navDrawerOpen = false;
                                    return;
                                  }
                                  executionsStore.setSelectedExecutionId(run.id);
                                  this._setTab("runs");
                                  this._navDrawerOpen = false;
                                },
                              },
                            )}
                          </div>
                        `}
                  </section>
                  <section class="nav-section">
                    ${collapsed
                      ? nothing
                      : html`
                          <div class="nav-section__label" aria-hidden="true">
                            <span class="nav-section__label-text">${t("nav.learningExplore", undefined, this._locale)}</span>
                          </div>
                        `}
                    <div class="nav-section__items" style=${collapsed ? "" : "padding-left:12px;"}>
                      ${this._navItem("skills", icons.puzzle, t("tab.skills", undefined, this._locale))}
                      ${this._navItem("generateChat", icons.messageSquare, t("tab.generateChat", undefined, this._locale))}
                    </div>
                  </section>
                  <section class="nav-section">
                    ${collapsed
                      ? nothing
                      : html`
                          <div class="nav-section__label" aria-hidden="true">
                            <span class="nav-section__label-text">${t("nav.completedProjectRun", undefined, this._locale)}</span>
                          </div>
                        `}
                    <div class="nav-section__items" style=${collapsed ? "" : "padding-left:12px;"}>
                      ${this._navItem("closedRuns", icons.fileText, t("tab.closedRuns", undefined, this._locale))}
                    </div>
                  </section>
                </nav>
              </div>
              <div class="sidebar-shell__footer">
                ${collapsed
                  ? nothing
                  : html`
                      <div class="sidebar-status-legend" aria-label="Run status legend">
                        <span class="sidebar-status-legend__line"
                          >${t("nav.legend.phase", undefined, this._locale)}</span
                        >
                        <span class="sidebar-status-legend__line"
                          >${t("nav.legend.final", undefined, this._locale)}</span
                        >
                      </div>
                    `}
                <div
                  class="sidebar-version"
                  title=${this._hermesConnected ? "Hermes gateway reachable" : "Hermes unreachable"}
                >
                  ${collapsed
                    ? html`
                        <span
                          class="sidebar-version__status ${this._hermesConnected
                            ? "sidebar-connection-status--online"
                            : "sidebar-connection-status--offline"}"
                          aria-hidden="true"
                        ></span>
                      `
                    : html`
                        <span class="sidebar-version__label">${t("nav.gateway", undefined, this._locale)}</span>
                        <span class="sidebar-version__text">${this._hermesConnected ? t("nav.online", undefined, this._locale) : t("nav.offline", undefined, this._locale)}</span>
                        <span
                          class="sidebar-version__status ${this._hermesConnected
                            ? "sidebar-connection-status--online"
                            : "sidebar-connection-status--offline"}"
                          aria-hidden="true"
                        ></span>
                      `}
                </div>
              </div>
            </div>
          </aside>
        </div>

        <main class=${contentClass}>
          ${!this._hermesConnected && this._gatewayEverConnected && this._healthFailureStreak >= 3
            ? html`
                <div
                  style="margin:8px 12px; padding:8px 12px; border:1px solid var(--border, #2e2f33); border-radius:8px; color:var(--warning, #f2b66d); background:var(--bg-surface, #222326);"
                  title="Gateway health probe is failing right now"
                >
                  ${t("gateway.unreachable.temporary", undefined, this._locale)}
                </div>
              `
            : nothing}
          ${this._renderView(tab)}
        </main>
      </div>
      <ead-app-settings-modal
        .open=${this._settingsOpen}
        @settings-close=${() => {
          this._settingsOpen = false;
        }}
      ></ead-app-settings-modal>
      <ead-operator-dialog-host></ead-operator-dialog-host>
      </div>
    `;
  }

  private _renderView(tab: Tab) {
    if (!this._hermesConnected && !this._gatewayEverConnected) {
      return html`
        <div class="ead-expui-placeholder">
            <strong>${t("gateway.notConnected.title", undefined, this._locale)}</strong>
          <span
            >${t("gateway.notConnected.help", undefined, this._locale)}</span
          >
        </div>
      `;
    }
    switch (tab) {
      case "projects":
        return html`<ead-projects-view
          .locale=${this._locale}
          @navigate-tab=${(e: CustomEvent) => {
            this._setTab(e.detail as Tab);
          }}
        ></ead-projects-view>`;
      case "runs":
        return html`<ead-runs-view .locale=${this._locale}></ead-runs-view>`;
      case "closedRuns":
        return html`<ead-closed-runs-view></ead-closed-runs-view>`;
      case "eadDiagramStandard":
        return html`<ead-diagram-standard-view></ead-diagram-standard-view>`;
      case "eadSkills":
        return html`<ead-skills-view></ead-skills-view>`;
      case "eadWorkflowRules":
        return html`<ead-workflow-rules-view></ead-workflow-rules-view>`;
      case "exploreTypes":
        return html`<ead-explore-types-view></ead-explore-types-view>`;
      case "skills":
        return html`
          <div class="ead-expui-placeholder">
            <strong>Skills</strong>
            <span>Placeholder — project and Hermes skills will be listed here.</span>
          </div>
        `;
      case "chat":
        return html`<ead-chat-view></ead-chat-view>`;
      case "chatMinimal":
        return html`<ead-chat-minimal></ead-chat-minimal>`;
      case "generateChat":
        return html`<ead-generate-chat-view></ead-generate-chat-view>`;
      default:
        return html`<div class="ead-expui-placeholder">${t("app.selectSection", undefined, this._locale)}</div>`;
    }
  }
}
